import { useState, useEffect, useRef } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import toast from "react-hot-toast";
import axios from "axios";
import { usePushNotifications } from "../hooks/usePushNotifications";
import { 
  Store, Save, CreditCard, Phone, CheckCircle, 
  MessageCircle, MapPin, Link, Facebook, Instagram, Bell, BellOff, Loader2,
  Calendar, Copy, Share2,
  Sun, Moon, Upload, Image, X, Globe
} from "lucide-react";

const API = `${window.location.origin}/api`;

export default function SettingsPage() {
  const { user, theme, toggleTheme } = useAuth();
  const { t } = useTranslation("ar");
  const { 
    isSupported: pushSupported, 
    isSubscribed: pushSubscribed, 
    permission: pushPermission,
    loading: pushLoading,
    subscribe: subscribeToPush,
    unsubscribe: unsubscribeFromPush
  } = usePushNotifications();
  
  const [formData, setFormData] = useState({
    shop_name: "",
    phone: "",
    whatsapp_number: "",
    address: "",
    website: "",
    facebook: "",
    instagram: "",
    tiktok: "",
  });
  const [loading, setLoading] = useState(false);
  const [shopLogo, setShopLogo] = useState(null);
  const [logoLoading, setLogoLoading] = useState(false);
  const logoInputRef = useRef(null);
  
  // إعدادات التقارير المجدولة
  const [scheduledReports, setScheduledReports] = useState({
    daily_report_enabled: false,
    daily_report_time: "21:00",
    whatsapp_recipient: "",
    include_sales: true,
    include_profit: true,
    include_debts: true,
    include_low_stock: true,
  });
  const [scheduledReportLoading, setScheduledReportLoading] = useState(false);

  // إعدادات الموقع
  const [locationData, setLocationData] = useState({
    latitude: null,
    longitude: null,
    address: "",
    google_maps_link: "",
  });
  const [locationLoading, setLocationLoading] = useState(false);

  const handlePushToggle = async () => {
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        toast.success(true ? "تم إيقاف التنبيهات" : "Notifications disabled");
      } else {
        await subscribeToPush();
        toast.success(true ? "تم تفعيل التنبيهات" : "Notifications enabled");
      }
    } catch (error) {
      toast.error(true ? "فشل تغيير إعدادات التنبيهات" : "Failed to change notification settings");
    }
  };

  useEffect(() => {
    fetchSettings();
    fetchScheduledReportSettings();
    fetchLocationSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData({
        shop_name: response.data.shop_name || "",
        phone: response.data.phone || "",
        whatsapp_number: response.data.whatsapp_number || "",
        address: response.data.address || "",
        website: response.data.website || "",
        facebook: response.data.facebook || "",
        instagram: response.data.instagram || "",
        tiktok: response.data.tiktok || "",
      });
      // تحميل الشعار إذا كان موجوداً
      if (response.data.shop_logo) {
        setShopLogo(response.data.shop_logo);
      }
      // تحميل إعدادات التقارير المجدولة
      if (response.data.scheduled_reports) {
        setScheduledReports(prev => ({ ...prev, ...response.data.scheduled_reports }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  // جلب إعدادات التقارير المجدولة
  const fetchScheduledReportSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/scheduled-reports`);
      if (response.data) {
        setScheduledReports(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error("Error fetching scheduled report settings:", error);
    }
  };

  // حفظ إعدادات التقارير المجدولة
  const handleSaveScheduledReports = async () => {
    setScheduledReportLoading(true);
    try {
      await axios.put(`${API}/settings/scheduled-reports`, scheduledReports);
      
      // حفظ في localStorage للتذكير المحلي
      localStorage.setItem('scheduled_reports', JSON.stringify(scheduledReports));
      
      toast.success(true ? "تم حفظ إعدادات التقارير المجدولة" : "Scheduled reports settings saved");
    } catch (error) {
      toast.error(true ? "خطأ في حفظ الإعدادات" : "Error saving settings");
    } finally {
      setScheduledReportLoading(false);
    }
  };

  // إرسال التقرير اليومي الآن
  const sendDailyReportNow = async () => {
    try {
      const response = await axios.get(`${API}/analytics/financial-summary`);
      const today = response.data.today || {};
      const debts = response.data.debts || {};
      
      const formatNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);
      const now = new Date();
      const isArabic = true;
      
      let message = isArabic 
        ? `📊 *التقرير اليومي - ${formData.shop_name || 'المتجر'}*\n`
        : `📊 *Daily Report - ${formData.shop_name || 'Store'}*\n`;
      
      message += `━━━━━━━━━━━━━━━\n`;
      message += `📅 ${now.toLocaleDateString(isArabic ? "ar-IQ" : "en-US", { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}\n`;
      message += `━━━━━━━━━━━━━━━\n\n`;
      
      if (scheduledReports.include_sales) {
        message += isArabic 
          ? `💰 *المبيعات:* ${formatNum(today.sales || 0)} د.ع\n`
          : `💰 *Sales:* ${formatNum(today.sales || 0)} د.ع\n`;
        message += isArabic
          ? `🔢 *المعاملات:* ${today.transactions || 0}\n`
          : `🔢 *Transactions:* ${today.transactions || 0}\n`;
      }
      
      if (scheduledReports.include_profit) {
        message += isArabic
          ? `📈 *الربح:* ${formatNum(today.profit || 0)} د.ع\n`
          : `📈 *Profit:* ${formatNum(today.profit || 0)} د.ع\n`;
      }
      
      if (scheduledReports.include_debts) {
        message += `\n`;
        message += isArabic
          ? `💳 *الديون المستحقة:* ${formatNum(debts.receivable || 0)} د.ع\n`
          : `💳 *Receivables:* ${formatNum(debts.receivable || 0)} د.ع\n`;
      }
      
      message += `━━━━━━━━━━━━━━━\n`;
      message += `🕐 ${now.toLocaleTimeString(isArabic ? "ar-IQ" : "en-US")}\n`;
      message += isArabic ? `✅ تم الإرسال من نازاريو حساباتي` : `✅ Sent from Nazario-Hesabaty`;
      
      const encodedMessage = encodeURIComponent(message);
      
      // إذا تم تحديد رقم المستلم، أرسل له مباشرة
      let phone = scheduledReports.whatsapp_recipient?.replace(/\D/g, '');
      if (phone) {
        if (phone.startsWith('07')) {
          phone = '964' + phone.substring(1);
        } else if (!phone.startsWith('964')) {
          phone = '964' + phone;
        }
        window.open(`https://wa.me/${phone}?text=${encodedMessage}`, '_blank');
      } else {
        window.open(`https://wa.me/?text=${encodedMessage}`, '_blank');
      }
      
      toast.success(isArabic ? "جاري فتح WhatsApp..." : "Opening WhatsApp...");
    } catch (error) {
      toast.error(true ? "خطأ في إنشاء التقرير" : "Error generating report");
    }
  };

  // رفع شعار المتجر
  const handleLogoUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // التحقق من نوع الملف
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      toast.error(true ? "نوع الملف غير مدعوم. استخدم PNG, JPG, WEBP, أو SVG" : "Unsupported file type");
      return;
    }

    // التحقق من حجم الملف (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      toast.error(true ? "حجم الملف كبير جداً. الحد الأقصى 2 ميجابايت" : "File too large. Max 2MB");
      return;
    }

    setLogoLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/settings/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setShopLogo(response.data.logo_url);
      toast.success(true ? "تم رفع الشعار بنجاح" : "Logo uploaded successfully");
    } catch (error) {
      console.error("Logo upload error:", error);
      toast.error(true ? "فشل رفع الشعار" : "Failed to upload logo");
    } finally {
      setLogoLoading(false);
    }
  };

  // حذف شعار المتجر
  const handleLogoDelete = async () => {
    setLogoLoading(true);
    try {
      await axios.delete(`${API}/settings/logo`);
      setShopLogo(null);
      toast.success(true ? "تم حذف الشعار" : "Logo deleted");
    } catch (error) {
      toast.error(true ? "فشل حذف الشعار" : "Failed to delete logo");
    } finally {
      setLogoLoading(false);
    }
  };

  // ==================== إعدادات الموقع ====================
  const fetchLocationSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/location`);
      if (response.data) {
        setLocationData({
          latitude: response.data.latitude || null,
          longitude: response.data.longitude || null,
          address: response.data.address || "",
          google_maps_link: response.data.google_maps_link || "",
        });
      }
    } catch (error) {
      console.log("No location settings found");
    }
  };

  const getCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error("المتصفح لا يدعم تحديد الموقع");
      return;
    }

    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const googleMapsLink = `https://www.google.com/maps?q=${latitude},${longitude}`;
        setLocationData({
          ...locationData,
          latitude,
          longitude,
          google_maps_link: googleMapsLink,
        });
        setLocationLoading(false);
        toast.success("تم تحديد الموقع بنجاح!");
      },
      (error) => {
        setLocationLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error("يرجى السماح بالوصول إلى الموقع");
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error("معلومات الموقع غير متوفرة");
            break;
          case error.TIMEOUT:
            toast.error("انتهت مهلة طلب الموقع");
            break;
          default:
            toast.error("خطأ في تحديد الموقع");
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  const saveLocationSettings = async () => {
    setLocationLoading(true);
    try {
      await axios.put(`${API}/settings/location`, locationData);
      toast.success("تم حفظ الموقع بنجاح");
    } catch (error) {
      toast.error("خطأ في حفظ الموقع");
    } finally {
      setLocationLoading(false);
    }
  };

  const shareLocation = () => {
    if (!locationData.google_maps_link && !locationData.address) {
      toast.error("يرجى تحديد الموقع أولاً");
      return;
    }

    const shopName = formData.shop_name || "متجرنا";
    const locationText = locationData.google_maps_link || locationData.address;
    const shareText = `📍 موقع ${shopName}\n\n${locationData.address ? `العنوان: ${locationData.address}\n` : ""}${locationData.google_maps_link ? `رابط الخريطة: ${locationData.google_maps_link}` : ""}`;

    if (navigator.share) {
      navigator.share({
        title: `موقع ${shopName}`,
        text: shareText,
        url: locationData.google_maps_link,
      }).catch(() => {
        navigator.clipboard.writeText(shareText);
        toast.success("تم نسخ معلومات الموقع");
      });
    } else {
      navigator.clipboard.writeText(shareText);
      toast.success("تم نسخ معلومات الموقع");
    }
  };

  const shareLocationWhatsApp = () => {
    if (!locationData.google_maps_link && !locationData.address) {
      toast.error("يرجى تحديد الموقع أولاً");
      return;
    }

    const shopName = formData.shop_name || "متجرنا";
    const shareText = `📍 موقع ${shopName}\n\n${locationData.address ? `العنوان: ${locationData.address}\n` : ""}${locationData.google_maps_link ? `رابط الخريطة: ${locationData.google_maps_link}` : ""}`;
    const encodedText = encodeURIComponent(shareText);
    window.open(`https://wa.me/?text=${encodedText}`, '_blank');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/settings`, formData);
      toast.success(true ? "تم الحفظ بنجاح" : "Saved successfully");
    } catch (error) {
      toast.error(t("error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="settings-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-heading font-bold text-white">{t("settings")}</h1>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Shop Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Store className="w-5 h-5 text-amber-500" />
              {true ? "معلومات المحل" : "Shop Information"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* قسم الشعار */}
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Image className="w-4 h-4 text-purple-500" />
                {true ? "شعار المتجر" : "Shop Logo"}
              </Label>
              <div className="flex items-center gap-4">
                {shopLogo ? (
                  <div className="relative group">
                    <img 
                      src={shopLogo} 
                      alt="Shop Logo" 
                      className="w-20 h-20 rounded-lg object-contain bg-slate-900 border border-slate-700 p-2"
                    />
                    <button
                      onClick={handleLogoDelete}
                      disabled={logoLoading}
                      className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => logoInputRef.current?.click()}
                    className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-600 flex items-center justify-center cursor-pointer hover:border-amber-500 transition-colors bg-slate-900/50"
                  >
                    {logoLoading ? (
                      <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
                    ) : (
                      <Upload className="w-6 h-6 text-slate-400" />
                    )}
                  </div>
                )}
                <div className="flex-1">
                  <input
                    ref={logoInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => logoInputRef.current?.click()}
                    disabled={logoLoading}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    {logoLoading ? (
                      <Loader2 className="w-4 h-4 me-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 me-2" />
                    )}
                    {shopLogo 
                      ? (true ? "تغيير الشعار" : "Change Logo")
                      : (true ? "رفع شعار" : "Upload Logo")}
                  </Button>
                  <p className="text-xs text-slate-400 mt-2">
                    {true 
                      ? "PNG, JPG, WEBP, SVG - الحد الأقصى 2 ميجابايت"
                      : "PNG, JPG, WEBP, SVG - Max 2MB"}
                  </p>
                  <p className="text-xs text-emerald-400">
                    {true 
                      ? "سيظهر الشعار في تصدير PDF للتقارير والفواتير"
                      : "Logo will appear in PDF exports for reports and receipts"}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">{t("shopName")}</Label>
              <Input
                name="shop_name"
                value={formData.shop_name}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder={true ? "أدخل اسم المحل" : "Enter shop name"}
                data-testid="shop-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <Phone className="w-4 h-4 text-blue-500" />
                {true ? "رقم الهاتف" : "Phone Number"}
              </Label>
              <Input
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder={true ? "مثال: 07xxxxxxxxx" : "e.g., 07xxxxxxxxx"}
                data-testid="phone-input"
                dir="ltr"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <MessageCircle className="w-4 h-4 text-green-500" />
                {true ? "رقم واتساب الإدارة" : "Admin WhatsApp Number"}
              </Label>
              <Input
                name="whatsapp_number"
                value={formData.whatsapp_number}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white"
                placeholder={true ? "مثال: 07xxxxxxxxx" : "e.g., 07xxxxxxxxx"}
                data-testid="whatsapp-number-input"
                dir="ltr"
              />
              <p className="text-xs text-slate-400">
                {true 
                  ? "سيتم استخدام هذا الرقم لإرسال واستلام الوصولات والتذكيرات"
                  : "This number will be used to send and receive receipts and reminders"}
              </p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-200 flex items-center gap-2">
                <MapPin className="w-4 h-4 text-red-500" />
                {true ? "العنوان" : "Address"}
              </Label>
              <Textarea
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="bg-slate-900 border-slate-700 text-white min-h-[80px]"
                placeholder={true ? "أدخل عنوان المحل" : "Enter shop address"}
                data-testid="address-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardContent className="p-4">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 py-6 text-lg font-bold"
              data-testid="save-settings-btn"
            >
              {loading ? (
                <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 me-2" />
                  {true ? "حفظ الإعدادات" : "Save Settings"}
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Push Notifications */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Bell className="w-5 h-5 text-amber-500" />
              {true ? "التنبيهات" : "Notifications"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {pushSupported ? (
              <>
                <div className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50">
                  <div className="flex items-center gap-3">
                    {pushSubscribed ? (
                      <Bell className="w-6 h-6 text-emerald-500" />
                    ) : (
                      <BellOff className="w-6 h-6 text-slate-500" />
                    )}
                    <div>
                      <p className="text-white font-medium">
                        {true ? "تنبيهات الدفع" : "Push Notifications"}
                      </p>
                      <p className="text-xs text-slate-400">
                        {pushSubscribed 
                          ? (true ? "التنبيهات مفعّلة" : "Notifications are enabled")
                          : (true ? "التنبيهات معطّلة" : "Notifications are disabled")
                        }
                      </p>
                    </div>
                  </div>
                  <Button
                    onClick={handlePushToggle}
                    disabled={pushLoading}
                    className={pushSubscribed 
                      ? "bg-red-600 hover:bg-red-700" 
                      : "bg-emerald-600 hover:bg-emerald-700"
                    }
                  >
                    {pushLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : pushSubscribed ? (
                      true ? "إيقاف" : "Disable"
                    ) : (
                      true ? "تفعيل" : "Enable"
                    )}
                  </Button>
                </div>
                
                {pushPermission === 'denied' && (
                  <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/30">
                    <p className="text-red-400 text-sm">
                      {true 
                        ? "تم حظر التنبيهات. يرجى السماح بها من إعدادات المتصفح."
                        : "Notifications blocked. Please allow them in browser settings."}
                    </p>
                  </div>
                )}
                
                <div className="text-xs text-slate-500">
                  <p>
                    {true 
                      ? "ستصلك تنبيهات عن: انتهاء صلاحية المنتجات، نفاد المخزون، الديون المستحقة"
                      : "You'll receive alerts for: Product expiry, Low stock, Due debts"}
                  </p>
                </div>
              </>
            ) : (
              <div className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30">
                <p className="text-amber-400 text-sm">
                  {true 
                    ? "التنبيهات غير مدعومة في هذا المتصفح"
                    : "Push notifications are not supported in this browser"}
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* التقارير المجدولة */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-500" />
              {true ? "التقارير المجدولة" : "Scheduled Reports"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* تفعيل/إيقاف التقرير اليومي */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-slate-900/50">
              <div className="flex items-center gap-3">
                <MessageCircle className={`w-6 h-6 ${scheduledReports.daily_report_enabled ? "text-green-500" : "text-slate-500"}`} />
                <div>
                  <p className="text-white font-medium">
                    {true ? "تقرير WhatsApp يومي" : "Daily WhatsApp Report"}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {true ? "تذكير يومي لإرسال ملخص المبيعات" : "Daily reminder to send sales summary"}
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                onClick={() => setScheduledReports(prev => ({ ...prev, daily_report_enabled: !prev.daily_report_enabled }))}
                className={scheduledReports.daily_report_enabled 
                  ? "bg-red-600 hover:bg-red-700" 
                  : "bg-emerald-600 hover:bg-emerald-700"
                }
              >
                {scheduledReports.daily_report_enabled 
                  ? (true ? "إيقاف" : "Disable")
                  : (true ? "تفعيل" : "Enable")}
              </Button>
            </div>

            {scheduledReports.daily_report_enabled && (
              <div className="space-y-4 p-4 rounded-lg bg-green-500/5 border border-green-500/20">
                {/* وقت الإرسال */}
                <div className="space-y-2">
                  <Label className="text-slate-200">
                    {true ? "وقت التذكير" : "Reminder Time"}
                  </Label>
                  <Input
                    type="time"
                    value={scheduledReports.daily_report_time}
                    onChange={(e) => setScheduledReports(prev => ({ ...prev, daily_report_time: e.target.value }))}
                    className="bg-slate-900 border-slate-700 text-white w-40"
                  />
                </div>

                {/* رقم المستلم */}
                <div className="space-y-2">
                  <Label className="text-slate-200">
                    {true ? "رقم WhatsApp المستلم (اختياري)" : "Recipient WhatsApp (optional)"}
                  </Label>
                  <Input
                    value={scheduledReports.whatsapp_recipient}
                    onChange={(e) => setScheduledReports(prev => ({ ...prev, whatsapp_recipient: e.target.value }))}
                    className="bg-slate-900 border-slate-700 text-white"
                    placeholder="07xxxxxxxxx"
                    dir="ltr"
                  />
                  <p className="text-xs text-slate-500">
                    {true 
                      ? "اترك فارغاً لإرسال التقرير لأي شخص"
                      : "Leave empty to send to anyone"}
                  </p>
                </div>

                {/* محتوى التقرير */}
                <div className="space-y-3">
                  <Label className="text-slate-200">
                    {true ? "محتوى التقرير" : "Report Content"}
                  </Label>
                  <div className="grid grid-cols-2 gap-3">
                    <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduledReports.include_sales}
                        onChange={(e) => setScheduledReports(prev => ({ ...prev, include_sales: e.target.checked }))}
                        className="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                      />
                      {true ? "المبيعات" : "Sales"}
                    </label>
                    <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduledReports.include_profit}
                        onChange={(e) => setScheduledReports(prev => ({ ...prev, include_profit: e.target.checked }))}
                        className="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                      />
                      {true ? "الأرباح" : "Profit"}
                    </label>
                    <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduledReports.include_debts}
                        onChange={(e) => setScheduledReports(prev => ({ ...prev, include_debts: e.target.checked }))}
                        className="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                      />
                      {true ? "الديون" : "Debts"}
                    </label>
                    <label className="flex items-center gap-2 text-slate-300 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={scheduledReports.include_low_stock}
                        onChange={(e) => setScheduledReports(prev => ({ ...prev, include_low_stock: e.target.checked }))}
                        className="rounded border-slate-600 bg-slate-900 text-emerald-500 focus:ring-emerald-500"
                      />
                      {true ? "تنبيهات المخزون" : "Stock Alerts"}
                    </label>
                  </div>
                </div>

                {/* أزرار الإجراءات */}
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveScheduledReports}
                    disabled={scheduledReportLoading}
                    className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                  >
                    {scheduledReportLoading ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <Save className="w-4 h-4 me-2" />
                        {true ? "حفظ" : "Save"}
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={sendDailyReportNow}
                    variant="outline"
                    className="border-green-500 text-green-400 hover:bg-green-500/20"
                  >
                    <MessageCircle className="w-4 h-4 me-2" />
                    {true ? "إرسال الآن" : "Send Now"}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Theme Settings */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              {theme === "dark" ? <Moon className="w-5 h-5 text-blue-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
              {true ? "المظهر" : "Appearance"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button
                variant={theme === "dark" ? "default" : "outline"}
                onClick={() => theme !== "dark" && toggleTheme()}
                className={`h-20 flex-col gap-2 ${theme === "dark" ? "bg-slate-700 hover:bg-slate-600 border-emerald-500" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
                data-testid="dark-theme-btn"
              >
                <Moon className="w-6 h-6" />
                {true ? "داكن" : "Dark"}
              </Button>
              <Button
                variant={theme === "light" ? "default" : "outline"}
                onClick={() => theme !== "light" && toggleTheme()}
                className={`h-20 flex-col gap-2 ${theme === "light" ? "bg-amber-500 hover:bg-amber-600 text-white" : "border-slate-600 text-slate-300 hover:bg-slate-700"}`}
                data-testid="light-theme-btn"
              >
                <Sun className="w-6 h-6" />
                {true ? "فاتح" : "Light"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Subscription Info */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-500" />
              {t("subscription")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Status */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-900/50 border border-slate-700">
                <CheckCircle className={`w-6 h-6 ${user?.subscription_active ? "text-green-500" : "text-amber-500"}`} />
                <div>
                  <p className="text-white font-medium text-sm">
                    {user?.subscription_active ? t("subscriptionActive") : t("subscriptionPending")}
                  </p>
                  <p className="text-amber-400 font-bold">{t("subscriptionPrice")}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* About */}
        <Card className="bg-slate-800/50 border-slate-700 lg:col-span-2">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="white" className="w-10 h-10">
                  <path d="M4 4a2 2 0 012-2h12a2 2 0 012 2v16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm14 0H6v16h12V4zM8 6h8v4H8V6zm0 6h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2zm-8 4h2v2H8v-2zm4 0h2v2h-2v-2zm4 0h2v2h-2v-2z"/>
                </svg>
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-white">{t("appName")}</h2>
                <p className="text-emerald-500 font-semibold">{true ? "نظام الحسابات" : "Accounting System"}</p>
                <p className="text-slate-400 text-sm mt-1">{t("appSubtitle")}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
